import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from "react";
import Select from "react-select";
import {
  getPaymentsProgressUsers,
  getPaymentsProgressUserProjects,
  getPaymentsProgressProjects,
  getPaymentsProgressProjectUsers,
  getPaymentsProgressGet,
  updatePaymentsProgressCorrection,
  createPaymentsProgressApproval,
  getPaymentsProgressApproval,
  deletePaymentsProgressApproval,
} from "../../../../api/paymentsProgressApi";
import Preloader from "../../../Preloader/Preloader";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import { getUsersSummary } from "../../../../api/api";
import { selectStyles } from "../../../../utils/selectStyles";
import GeneralDesignSection from "./GeneralDesignSection";
import "./PaymentsProgress.css";

const MONTH_NAMES = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

const formatMoney = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "–";
  return Number(value).toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatSecondsToHHMM = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "–";
  const totalMinutes = Math.floor(Number(seconds) / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const getDefaultMonthYear = () => {
  const d = new Date();
  return { month: d.getMonth() + 1, year: d.getFullYear() };
};

const GROUP_BY_USERS = "users";
const GROUP_BY_PROJECTS = "projects";

/** Суммы по всем проектам сотрудника за период (как в строках таблицы и в ИТОГО). */
async function fetchUserPaymentTotalsAcrossProjects(month, year, userId) {
  const projects = await getPaymentsProgressUserProjects(month, year, userId);
  const projectList = Array.isArray(projects) ? projects : [];
  const perProject = await Promise.all(
    projectList.map(async (project) => {
      let byEmployee = 0;
      let byCorrection = 0;
      try {
        const statsRaw = await getPaymentsProgressGet(month, year, userId, project.id);
        const stats = Array.isArray(statsRaw) ? statsRaw : [];
        for (const s of stats) {
          byEmployee += Number(s.to_be_payed_by_employee) || 0;
          const corrSum = s.correction?.completion_sum_after_correction;
          byCorrection +=
            corrSum != null ? Number(corrSum) : Number(s.to_be_payed_by_employee) || 0;
        }
      } catch {
        /* проект пропускаем при ошибке запроса */
      }
      return { byEmployee, byCorrection };
    }),
  );
  return perProject.reduce(
    (acc, x) => ({
      byEmployee: acc.byEmployee + x.byEmployee,
      byCorrection: acc.byCorrection + x.byCorrection,
    }),
    { byEmployee: 0, byCorrection: 0 },
  );
}

const PaymentsProgress = () => {
  const { userData } = useContext(UserContext);
  const isAdms = userData?.access_levels?.some((level) => level?.name === "АДМС");
  const isFin = userData?.access_levels?.some((level) => level?.name === "ФИН");
  const canViewGeneralDesign =
    isAdms || isFin || [94, 96].includes(Number(userData?.id));
  const isGeneralDesignReadOnly = Number(userData?.id) === 94 || isFin;
  const isSketchReadOnly = isFin;
  const isGeneralDesignOnlyUser = Number(userData?.id) === 96;
  const [monthYear, setMonthYear] = useState(getDefaultMonthYear);
  const isProjectsOnlyUser = [96, 98, 99].includes(userData?.id);
  const [grouping, setGrouping] = useState(
    isProjectsOnlyUser ? GROUP_BY_PROJECTS : GROUP_BY_USERS
  );
  const canSwitchGrouping = !isProjectsOnlyUser;

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [userProjects, setUserProjects] = useState({});
  const [projectUsers, setProjectUsers] = useState({});
  const [userProjectStats, setUserProjectStats] = useState({});
  const [loadingProjectsByUser, setLoadingProjectsByUser] = useState({});
  const [loadingUsersByProject, setLoadingUsersByProject] = useState({});
  const [loadingStatsByKey, setLoadingStatsByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [expandedTopProjects, setExpandedTopProjects] = useState(new Set());
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [sketchDesignExpanded, setSketchDesignExpanded] = useState(true);

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    type: "percent",
    correctionId: null,
    userId: null,
    userName: "",
    workTypeName: "",
    currentValue: "",
    newValue: "",
    estimateContract: 0,
    sharePercent: 0,
    onConfirm: null,
  });
  const [commentModal, setCommentModal] = useState({
    open: false,
    workTypeName: "",
    correctionId: null,
    currentComment: "",
    onSave: null,
  });
  const [savingId, setSavingId] = useState(null);
  const [sendApprovalModalOpen, setSendApprovalModalOpen] = useState(false);
  const [sendingApproval, setSendingApproval] = useState(false);
  const [deletingApproval, setDeletingApproval] = useState(false);
  const [approverOptions, setApproverOptions] = useState([]);
  const [approvalUserOptions, setApprovalUserOptions] = useState([]);
  const [selectedApprovalUser, setSelectedApprovalUser] = useState(null);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [approvalByUserId, setApprovalByUserId] = useState({});
  /** Итого к выплате по всем проектам (режим «По сотрудникам», верхний список). */
  const [userAllProjectsTotals, setUserAllProjectsTotals] = useState({});
  const isBlockingAction = Boolean(savingId) || sendingApproval || deletingApproval;
  const userProjectsRef = useRef(userProjects);
  const loadingProjectsByUserRef = useRef(loadingProjectsByUser);
  const projectUsersRef = useRef(projectUsers);
  const loadingUsersByProjectRef = useRef(loadingUsersByProject);
  const userProjectStatsRef = useRef(userProjectStats);
  const loadingStatsByKeyRef = useRef(loadingStatsByKey);
  const suppressConfirmOverlayCloseRef = useRef(false);

  const { month, year } = monthYear;

  useEffect(() => {
    userProjectsRef.current = userProjects;
  }, [userProjects]);

  useEffect(() => {
    loadingProjectsByUserRef.current = loadingProjectsByUser;
  }, [loadingProjectsByUser]);

  useEffect(() => {
    projectUsersRef.current = projectUsers;
  }, [projectUsers]);

  useEffect(() => {
    loadingUsersByProjectRef.current = loadingUsersByProject;
  }, [loadingUsersByProject]);

  useEffect(() => {
    userProjectStatsRef.current = userProjectStats;
  }, [userProjectStats]);

  useEffect(() => {
    loadingStatsByKeyRef.current = loadingStatsByKey;
  }, [loadingStatsByKey]);

  useEffect(() => {
    const releaseOverlayCloseLock = () => {
      window.setTimeout(() => {
        suppressConfirmOverlayCloseRef.current = false;
      }, 0);
    };
    window.addEventListener("pointerup", releaseOverlayCloseLock);
    window.addEventListener("pointercancel", releaseOverlayCloseLock);
    return () => {
      window.removeEventListener("pointerup", releaseOverlayCloseLock);
      window.removeEventListener("pointercancel", releaseOverlayCloseLock);
    };
  }, []);

  const lockConfirmOverlayCloseDuringSliderDrag = () => {
    suppressConfirmOverlayCloseRef.current = true;
  };

  const selectConfirmInputValueOnFocus = (e) => {
    e.target.select();
  };

  const handleConfirmOverlayClick = () => {
    if (suppressConfirmOverlayCloseRef.current) return;
    setConfirmModal((m) => ({ ...m, open: false }));
  };

  useEffect(() => {
    setApprovalByUserId({});
  }, [month, year]);

  const loadApprovalsByUserIds = useCallback(async (userIds) => {
    const uniqueUserIds = Array.from(
      new Set((userIds || []).map(Number).filter((id) => !Number.isNaN(id)))
    );
    const toLoad = uniqueUserIds.filter((id) => approvalByUserId[id] === undefined);
    if (toLoad.length === 0) return;

    const BATCH_SIZE = 8;
    const loadedMap = {};
    for (let i = 0; i < toLoad.length; i += BATCH_SIZE) {
      const batch = toLoad.slice(i, i + BATCH_SIZE);
      const approvals = await Promise.all(
        batch.map((userId) => getPaymentsProgressApproval(year, month, userId))
      );
      batch.forEach((userId, idx) => {
        loadedMap[userId] = approvals[idx] || null;
      });
    }
    setApprovalByUserId((prev) => ({ ...prev, ...loadedMap }));
  }, [approvalByUserId, month, year]);

  useEffect(() => {
    if (isProjectsOnlyUser) {
      setGrouping(GROUP_BY_PROJECTS);
    }
  }, [isProjectsOnlyUser]);

  const dateOptions = useMemo(() => {
    const opts = [];
    const end = new Date();
    const start = new Date(end.getFullYear() - 2, 0, 1);
    for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
      for (let m = 1; m <= 12; m++) {
        opts.push({
          value: `${y}-${String(m).padStart(2, "0")}`,
          label: `${MONTH_NAMES[m - 1]} ${y}`,
          month: m,
          year: y,
        });
      }
    }
    opts.sort((a, b) => b.value.localeCompare(a.value));
    return opts.slice(0, 36);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadingError(null);
    setUsers([]);
    setProjects([]);
    setUserProjects({});
    setProjectUsers({});
    setUserProjectStats({});
    setUserAllProjectsTotals({});
    setLoadingProjectsByUser({});
    setLoadingUsersByProject({});
    setLoadingStatsByKey({});
    setExpandedUsers(new Set());
    setExpandedTopProjects(new Set());
    setExpandedProjects(new Set());
    const request =
      grouping === GROUP_BY_USERS
        ? getPaymentsProgressUsers(month, year)
        : getPaymentsProgressProjects(month, year);

    request
      .then((list) => {
        if (cancelled) return;
        if (grouping === GROUP_BY_USERS) {
          setUsers(Array.isArray(list) ? list : []);
        } else {
          setProjects(Array.isArray(list) ? list : []);
        }
      })
      .catch((err) => {
        if (!cancelled) setLoadingError(err?.message || "Ошибка загрузки");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [month, year, grouping]);

  // Подгрузка проектов при раскрытии пользователя
  useEffect(() => {
    if (grouping !== GROUP_BY_USERS || !month || !year || users.length === 0) return;
    let cancelled = false;
    const toLoad = Array.from(expandedUsers).filter(
      (userId) =>
        userProjectsRef.current[userId] === undefined &&
        !loadingProjectsByUserRef.current[userId]
    );
    toLoad.forEach((userId) => {
      setLoadingProjectsByUser((prev) => ({ ...prev, [userId]: true }));
      getPaymentsProgressUserProjects(month, year, userId)
        .then((list) => {
          if (cancelled) return;
          setUserProjects((prev) => ({
            ...prev,
            [userId]: Array.isArray(list) ? list : [],
          }));
        })
        .catch(() => {
          if (!cancelled) {
            setUserProjects((prev) => ({ ...prev, [userId]: [] }));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoadingProjectsByUser((prev) => ({ ...prev, [userId]: false }));
          }
        });
    });
    return () => {
      cancelled = true;
    };
  }, [month, year, grouping, users, expandedUsers]);

  // Подгрузка пользователей при раскрытии проекта
  useEffect(() => {
    if (grouping !== GROUP_BY_PROJECTS || !month || !year || projects.length === 0) return;
    let cancelled = false;
    const toLoad = Array.from(expandedTopProjects).filter(
      (projectId) =>
        projectUsersRef.current[projectId] === undefined &&
        !loadingUsersByProjectRef.current[projectId]
    );
    toLoad.forEach((projectId) => {
      setLoadingUsersByProject((prev) => ({ ...prev, [projectId]: true }));
      getPaymentsProgressProjectUsers(month, year, projectId)
        .then((list) => {
          if (cancelled) return;
          setProjectUsers((prev) => ({
            ...prev,
            [projectId]: Array.isArray(list) ? list : [],
          }));
        })
        .catch(() => {
          if (!cancelled) {
            setProjectUsers((prev) => ({ ...prev, [projectId]: [] }));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoadingUsersByProject((prev) => ({ ...prev, [projectId]: false }));
          }
        });
    });
    return () => {
      cancelled = true;
    };
  }, [month, year, grouping, projects, expandedTopProjects]);

  // Подгрузка расчётов при раскрытии проекта
  useEffect(() => {
    if (!month || !year) return;
    let cancelled = false;
    const toLoad = Array.from(expandedProjects).filter((key) => {
      const [userId, projectId] = key.split("-").map(Number);
      if (Number.isNaN(userId) || Number.isNaN(projectId)) return false;
      const stats = userProjectStatsRef.current[userId]?.[projectId];
      return stats === undefined && !loadingStatsByKeyRef.current[key];
    });
    toLoad.forEach((key) => {
      const [userId, projectId] = key.split("-").map(Number);
      setLoadingStatsByKey((prev) => ({ ...prev, [key]: true }));
      getPaymentsProgressGet(month, year, userId, projectId)
        .then((data) => {
          if (cancelled) return;
          setUserProjectStats((prev) => {
            const next = { ...prev };
            next[userId] = { ...(next[userId] || {}), [projectId]: Array.isArray(data) ? data : [] };
            return next;
          });
        })
        .catch(() => {
          if (!cancelled) {
            setUserProjectStats((prev) => {
              const next = { ...prev };
              next[userId] = { ...(next[userId] || {}), [projectId]: [] };
              return next;
            });
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoadingStatsByKey((prev) => ({ ...prev, [key]: false }));
          }
        });
    });
    return () => {
      cancelled = true;
    };
  }, [month, year, expandedProjects]);

  const toggleUser = (userId) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleProject = (userId, projectId) => {
    const key = `${userId}-${projectId}`;
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleTopProject = (projectId) => {
    setExpandedTopProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const grandTotals = useMemo(() => {
    let byEmployee = 0;
    let byCorrection = 0;
    Object.values(userProjectStats).forEach((statsByProject) => {
      Object.values(statsByProject || {}).forEach((stats) => {
        (Array.isArray(stats) ? stats : []).forEach((s) => {
          byEmployee += Number(s.to_be_payed_by_employee) || 0;
          const corr = s.correction?.completion_sum_after_correction;
          byCorrection += corr != null ? Number(corr) : Number(s.to_be_payed_by_employee) || 0;
        });
      });
    });
    return { byEmployee, byCorrection };
  }, [userProjectStats]);

  const refreshUserPaymentTotalsRow = useCallback(
    async (userId) => {
      if (userId == null || Number.isNaN(Number(userId))) return;
      try {
        const totals = await fetchUserPaymentTotalsAcrossProjects(month, year, userId);
        setUserAllProjectsTotals((prev) => ({
          ...prev,
          [userId]: { loading: false, ...totals, error: null },
        }));
      } catch {
        setUserAllProjectsTotals((prev) => ({
          ...prev,
          [userId]: {
            loading: false,
            byEmployee: null,
            byCorrection: null,
            error: true,
          },
        }));
      }
    },
    [month, year],
  );

  const handleDateChange = (e) => {
    const v = e.target.value;
    if (!v) return;
    const [y, m] = v.split("-").map(Number);
    setMonthYear({ month: m, year: y });
  };

  const openConfirmPercent = (stat, user) => {
    const corr = stat.correction;
    const current = corr?.completion_percentage_after_correction ?? stat.completion_percentage_by_employee ?? 0;
    const estimateContract = Number(stat.estimate_by_contract) || 0;
    const sharePercent = Number(stat.work_type?.share_percent) || 0;
    setConfirmModal({
      open: true,
      title: "Изменить процент выполнения?",
      type: "percent",
      correctionId: corr?.id,
      userId: user.id,
      userName: [user.surname, user.name].filter(Boolean).join(" ") || `ID ${user.id}`,
      workTypeName: stat.work_type?.name ?? "–",
      currentValue: String(current),
      newValue: String(current),
      estimateContract,
      sharePercent,
      onConfirm: (newVal) => {
        if (!corr?.id) return;
        setSavingId(corr.id);
        const pct = Number(newVal);
        const derivedSum =
          estimateContract > 0 && sharePercent > 0
            ? (estimateContract * sharePercent * pct) / 10000
            : 0;
        updatePaymentsProgressCorrection({
          id: corr.id,
          month,
          year,
          completion_percentage_after_correction: pct,
        })
          .then((data) => {
            const res = data?.correction ?? data;
            const serverPct = res?.completion_percentage_after_correction;
            const serverSum = res?.completion_sum_after_correction;
            const displayPct = serverPct != null ? serverPct : pct;
            const displaySum = serverSum != null ? serverSum : derivedSum;
            setUserProjectStats((prev) => {
              const next = { ...prev };
              const uid = String(user.id);
              if (!next[uid]) return prev;
              next[uid] = { ...next[uid] };
              Object.keys(next[uid]).forEach((pid) => {
                next[uid][pid] = next[uid][pid].map((s) =>
                  s.correction?.id === corr.id
                    ? {
                        ...s,
                        correction: {
                          ...s.correction,
                          completion_percentage_after_correction: displayPct,
                          completion_sum_after_correction: displaySum,
                        },
                      }
                    : s
                );
              });
              return next;
            });
            refreshUserPaymentTotalsRow(user.id);
          })
          .catch((err) => alert(err?.message || "Ошибка сохранения"))
          .finally(() => setSavingId(null));
      },
    });
  };

  const openConfirmSum = (stat, user) => {
    const corr = stat.correction;
    const current = corr?.completion_sum_after_correction ?? stat.to_be_payed_by_employee ?? 0;
    const estimateContract = Number(stat.estimate_by_contract) || 0;
    const sharePercent = Number(stat.work_type?.share_percent) || 0;
    setConfirmModal({
      open: true,
      title: "Изменить сумму к выплате?",
      type: "sum",
      correctionId: corr?.id,
      userId: user.id,
      userName: [user.surname, user.name].filter(Boolean).join(" ") || `ID ${user.id}`,
      workTypeName: stat.work_type?.name ?? "–",
      currentValue: String(current),
      newValue: String(current),
      estimateContract,
      sharePercent,
      onConfirm: (newVal) => {
        if (!corr?.id) return;
        setSavingId(corr.id);
        const sum = Number(newVal);
        const base = estimateContract * (sharePercent / 100);
        const derivedPct =
          base > 0 ? Math.min(100, (sum * 100) / base) : (sharePercent > 0 ? (sum * 100) / estimateContract : 0);
        updatePaymentsProgressCorrection({
          id: corr.id,
          month,
          year,
          completion_sum_after_correction: sum,
        })
          .then((data) => {
            const res = data?.correction ?? data;
            const serverSum = res?.completion_sum_after_correction;
            const serverPct = res?.completion_percentage_after_correction;
            const displaySum = serverSum != null ? serverSum : sum;
            const displayPct = serverPct != null ? serverPct : derivedPct;
            setUserProjectStats((prev) => {
              const next = { ...prev };
              const uid = String(user.id);
              if (!next[uid]) return prev;
              next[uid] = { ...next[uid] };
              Object.keys(next[uid]).forEach((pid) => {
                next[uid][pid] = next[uid][pid].map((s) =>
                  s.correction?.id === corr.id
                    ? {
                        ...s,
                        correction: {
                          ...s.correction,
                          completion_sum_after_correction: displaySum,
                          completion_percentage_after_correction: displayPct,
                        },
                      }
                    : s
                );
              });
              return next;
            });
            refreshUserPaymentTotalsRow(user.id);
          })
          .catch((err) => alert(err?.message || "Ошибка сохранения"))
          .finally(() => setSavingId(null));
      },
    });
  };

  const openCommentModal = (stat) => {
    const corr = stat.correction;
    setCommentModal({
      open: true,
      workTypeName: stat.work_type?.name ?? "–",
      correctionId: corr?.id,
      currentComment: corr?.comment ?? "",
      onSave: (comment) => {
        if (!corr?.id) return;
        setSavingId(corr.id);
        updatePaymentsProgressCorrection({ id: corr.id, month, year, comment })
          .then(() => {
            setUserProjectStats((prev) => {
              const next = { ...prev };
              Object.keys(next).forEach((uid) => {
                next[uid] = { ...next[uid] };
                Object.keys(next[uid] || {}).forEach((pid) => {
                  next[uid][pid] = (next[uid][pid] || []).map((s) =>
                    s.correction?.id === corr.id
                      ? { ...s, correction: { ...s.correction, comment } }
                      : s
                  );
                });
              });
              return next;
            });
          })
          .catch((err) => alert(err?.message || "Ошибка сохранения"))
          .finally(() => setSavingId(null));
      },
    });
  };

  const confirmModalConfirm = () => {
    const newVal = confirmModal.newValue.trim();
    if (confirmModal.type === "percent") {
      const n = Number(newVal);
      if (Number.isNaN(n) || n < 0 || n > 100) {
        alert("Введите процент от 0 до 100");
        return;
      }
      confirmModal.onConfirm?.(n);
    } else {
      const n = Number(newVal.replace(/\s/g, ""));
      const maxSum = Number(confirmModal.estimateContract) || 0;
      if (Number.isNaN(n) || n < 0) {
        alert("Введите корректную сумму");
        return;
      }
      if (maxSum > 0 && n > maxSum) {
        alert(`Сумма не может превышать смету по договору (₸ ${formatMoney(maxSum)})`);
        return;
      }
      confirmModal.onConfirm?.(n);
    }
    setConfirmModal((m) => ({ ...m, open: false }));
  };

  const [commentInput, setCommentInput] = useState("");
  useEffect(() => {
    if (commentModal.open) setCommentInput(commentModal.currentComment);
  }, [commentModal.open, commentModal.currentComment]);

  const commentModalSave = () => {
    commentModal.onSave?.(commentInput);
    setCommentModal((m) => ({ ...m, open: false }));
  };

  const openSendApprovalModal = async () => {
    if (isSketchReadOnly) return;
    try {
      const [paymentsUsers, usersSummary] = await Promise.all([
        getPaymentsProgressUsers(month, year),
        getUsersSummary({ isActive: true, pageSize: 1000 }),
      ]);

      const paymentsUserOpts = (Array.isArray(paymentsUsers) ? paymentsUsers : []).map((u) => ({
        value: u.id,
        label: [u.surname, u.name].filter(Boolean).join(" ").trim() || `ID ${u.id}`,
      }));
      setApprovalUserOptions(paymentsUserOpts);
      await loadApprovalsByUserIds(paymentsUserOpts.map((u) => u.value));

      const approvers = (usersSummary?.users || []).map((u) => ({
        value: u.id,
        label: [u.surname, u.name].filter(Boolean).join(" ").trim() || `ID ${u.id}`,
      }));
      setApproverOptions(approvers);

      setSelectedApprovalUser((prev) =>
        prev && paymentsUserOpts.some((o) => o.value === prev.value) ? prev : null
      );
      setSelectedApprovers([]);

      setSendApprovalModalOpen(true);
    } catch (err) {
      alert(err?.message || "Не удалось загрузить данные для согласования");
    }
  };

  const submitSendApproval = async () => {
    if (isSketchReadOnly) return;
    if (!selectedApprovalUser?.value) {
      alert("Выберите сотрудника для согласования");
      return;
    }
    const approverIds = selectedApprovers.map((a) => a.value);
    if (approverIds.length === 0) {
      alert("Выберите хотя бы одного согласующего");
      return;
    }

    try {
      setSendingApproval(true);
      const created = await createPaymentsProgressApproval(
        year,
        month,
        selectedApprovalUser.value,
        approverIds
      );
      setApprovalByUserId((prev) => ({
        ...prev,
        [selectedApprovalUser.value]: created || prev[selectedApprovalUser.value] || null,
      }));
      alert("Прогресс выплат успешно отправлен на согласование");
      setSendApprovalModalOpen(false);
    } catch (err) {
      alert(err?.message || "Ошибка отправки на согласование");
    } finally {
      setSendingApproval(false);
    }
  };

  const deleteExistingApproval = async () => {
    if (isSketchReadOnly) return;
    if (!selectedUserApproval?.id) return;
    const ok = window.confirm("Удалить текущее согласование для выбранного сотрудника?");
    if (!ok) return;

    try {
      setDeletingApproval(true);
      await deletePaymentsProgressApproval(selectedUserApproval.id);
      setApprovalByUserId((prev) => ({
        ...prev,
        [selectedApprovalUser.value]: null,
      }));
      setSelectedApprovers([]);
      alert("Согласование удалено");
    } catch (err) {
      alert(err?.message || "Ошибка удаления согласования");
    } finally {
      setDeletingApproval(false);
    }
  };

  const canDeleteApprovalForUser = (userId) => {
    if (isSketchReadOnly) return false;
    const approval = approvalByUserId[userId];
    if (!approval?.id) return false;
    const creatorId = approval?.creator_id ?? approval?.creator?.id ?? null;
    return creatorId != null && String(creatorId) === String(userData?.id);
  };

  const isUserOnApproval = (userId) => Boolean(approvalByUserId[userId]?.id);

  const deleteApprovalForUser = async (user) => {
    if (isSketchReadOnly) return;
    const approval = approvalByUserId[user?.id];
    if (!approval?.id) return;
    const userName =
      [user?.surname, user?.name].filter(Boolean).join(" ").trim() || `ID ${user?.id}`;
    const ok = window.confirm(`Удалить согласование для сотрудника «${userName}»?`);
    if (!ok) return;

    try {
      setDeletingApproval(true);
      await deletePaymentsProgressApproval(approval.id);
      setApprovalByUserId((prev) => ({ ...prev, [user.id]: null }));
      alert("Согласование удалено");
    } catch (err) {
      alert(err?.message || "Ошибка удаления согласования");
    } finally {
      setDeletingApproval(false);
    }
  };

  const selectedUserApproval = selectedApprovalUser?.value
    ? approvalByUserId[selectedApprovalUser.value]
    : null;
  const hasExistingApproval = Boolean(selectedUserApproval?.id);
  const approvalCreatorId =
    selectedUserApproval?.creator_id ?? selectedUserApproval?.creator?.id ?? null;
  const canDeleteExistingApproval =
    hasExistingApproval &&
    approvalCreatorId != null &&
    String(approvalCreatorId) === String(userData?.id);
  const getPaymentsApprovalAggregateTone = (approval) => {
    if (!approval) return "neutral";
    const statuses = (approval?.approvers || [])
      .map((a) => String(a?.status || "").toLowerCase())
      .filter(Boolean);
    if (statuses.length === 0) return "pending";
    if (statuses.some((s) => s === "rejected" || s === "отклонен")) return "rejected";
    if (
      statuses.every((s) => s === "approved" || s === "согласован") &&
      statuses.length > 0
    ) {
      return "approved";
    }
    return "pending";
  };

  const getApprovalStatusText = (approval) => {
    const tone = getPaymentsApprovalAggregateTone(approval);
    if (tone === "rejected") return "Отклонено";
    if (tone === "approved") return "Согласовано";
    return "На рассмотрении";
  };

  const getApproverStatusLabelRu = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved" || s === "согласован") return "Согласован";
    if (s === "rejected" || s === "отклонен") return "Отклонён";
    return "На рассмотрении";
  };

  const getApproverRowStatusTone = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved" || s === "согласован") return "approved";
    if (s === "rejected" || s === "отклонен") return "rejected";
    return "pending";
  };

  const renderApprovalStatusBadge = (approval, approvalStatusText) => {
    const approversList =
      approval && Array.isArray(approval.approvers) && approval.approvers.length > 0
        ? approval.approvers
        : null;
    const ariaApprovers =
      approversList &&
      approversList
        .map((a) => {
          const name =
            [a?.user?.surname, a?.user?.name].filter(Boolean).join(" ").trim() ||
            (a?.user_id != null ? `ID ${a.user_id}` : "Согласующий");
          return `${name}: ${getApproverStatusLabelRu(a?.status)}`;
        })
        .join("; ");
    const tone = approval ? getPaymentsApprovalAggregateTone(approval) : "neutral";
    const toneClass =
      tone === "approved"
        ? "pp-approval-badge--approved"
        : tone === "rejected"
          ? "pp-approval-badge--rejected"
          : tone === "pending"
            ? "pp-approval-badge--pending"
            : "pp-approval-badge--neutral";
    return (
      <span
        className={
          approversList
            ? "pp-approval-badge-tooltip-host pp-approval-badge-tooltip-host--rich"
            : "pp-approval-badge-tooltip-host"
        }
      >
        <span
          className={`pp-approval-badge ${toneClass}`}
          {...(ariaApprovers
            ? { "aria-label": `Согласующие: ${ariaApprovers}` }
            : {})}
        >
          {approvalStatusText}
        </span>
        {approversList && (
          <div className="pp-approval-tooltip-panel" role="tooltip">
            <ul className="pp-approval-tooltip-list">
              {approversList.map((a) => {
                const name =
                  [a?.user?.surname, a?.user?.name].filter(Boolean).join(" ").trim() ||
                  (a?.user_id != null ? `ID ${a.user_id}` : "Согласующий");
                const rowTone = getApproverRowStatusTone(a?.status);
                return (
                  <li
                    key={a.id ?? `${a.user_id}-${a.approval_id ?? ""}`}
                    className="pp-approval-tooltip-row"
                  >
                    <span className="pp-approval-tooltip-name">{name}</span>
                    <span className="pp-approval-tooltip-sep"> — </span>
                    <span
                      className={`pp-approval-tooltip-status pp-approval-tooltip-status--${rowTone}`}
                    >
                      {getApproverStatusLabelRu(a?.status)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </span>
    );
  };

  useEffect(() => {
    if (grouping !== GROUP_BY_USERS || !users.length) return;
    loadApprovalsByUserIds(users.map((u) => u.id)).catch(() => {});
  }, [grouping, users, month, year, loadApprovalsByUserIds]);

  useEffect(() => {
    if (grouping !== GROUP_BY_USERS || !month || !year || users.length === 0) return;
    let cancelled = false;
    const ids = users.map((u) => u.id);
    setUserAllProjectsTotals(() => {
      const next = {};
      ids.forEach((id) => {
        next[id] = {
          loading: true,
          byEmployee: null,
          byCorrection: null,
          error: null,
        };
      });
      return next;
    });

    const CONCURRENCY = 6;
    const loadOneUserTotal = async (userId) => {
      try {
        const totals = await fetchUserPaymentTotalsAcrossProjects(month, year, userId);
        if (!cancelled) {
          setUserAllProjectsTotals((prev) => ({
            ...prev,
            [userId]: { loading: false, ...totals, error: null },
          }));
        }
      } catch {
        if (!cancelled) {
          setUserAllProjectsTotals((prev) => ({
            ...prev,
            [userId]: {
              loading: false,
              byEmployee: null,
              byCorrection: null,
              error: true,
            },
          }));
        }
      }
    };

    const run = async () => {
      for (let i = 0; i < ids.length; i += CONCURRENCY) {
        if (cancelled) return;
        const chunk = ids.slice(i, i + CONCURRENCY);
        await Promise.all(chunk.map((userId) => loadOneUserTotal(userId)));
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [grouping, users, month, year]);

  useEffect(() => {
    if (grouping !== GROUP_BY_PROJECTS) return;
    const projectModeUserIds = Object.values(projectUsers)
      .flatMap((arr) => (Array.isArray(arr) ? arr : []))
      .map((u) => u.id);
    if (!projectModeUserIds.length) return;
    loadApprovalsByUserIds(projectModeUserIds).catch(() => {});
  }, [grouping, projectUsers, month, year, loadApprovalsByUserIds]);

  if (loading && users.length === 0) {
    return (
      <div className="payments-progress">
        <Preloader />
      </div>
    );
  }

  return (
    <div className="payments-progress">
      {isBlockingAction && (
        <div className="pp-blocking-overlay" role="status" aria-live="polite">
          <div className="pp-blocking-overlay__content">
            <Preloader />
            <div className="pp-blocking-overlay__text">Сохраняем изменения...</div>
          </div>
        </div>
      )}
      <div className="pp-date-row">
        <label>
          Выбор даты <span className="required">*</span>
        </label>
        <select
          className="pp-date-select"
          value={`${year}-${String(month).padStart(2, "0")}`}
          onChange={handleDateChange}
          aria-label="Месяц и год"
        >
          {dateOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {loadingError && (
        <div className="pp-empty">{loadingError}</div>
      )}

      {!loadingError && users.length === 0 && !loading && !isGeneralDesignOnlyUser && (
        grouping === GROUP_BY_USERS && (
          <div className="pp-empty">Нет данных за выбранный период</div>
        )
      )}
      {!loadingError && projects.length === 0 && !loading && !isGeneralDesignOnlyUser && (
        grouping === GROUP_BY_PROJECTS && (
          <div className="pp-empty">Нет данных за выбранный период</div>
        )
      )}

      {canSwitchGrouping && (
        <div className="pp-grouping-row">
          <span>Группировка:</span>
          <label>
            <input
              type="radio"
              name="payments-grouping"
              value={GROUP_BY_USERS}
              checked={grouping === GROUP_BY_USERS}
              onChange={(e) => setGrouping(e.target.value)}
            />
            По сотрудникам
          </label>
          <label>
            <input
              type="radio"
              name="payments-grouping"
              value={GROUP_BY_PROJECTS}
              checked={grouping === GROUP_BY_PROJECTS}
              onChange={(e) => setGrouping(e.target.value)}
            />
            По проектам
          </label>
        </div>
      )}

      {!loadingError &&
        grouping === GROUP_BY_USERS &&
        (users.length > 0 || isGeneralDesignOnlyUser) && (
        <>
          {!isGeneralDesignOnlyUser && (
            <div className="pp-table-wrap">
              <table className="pp-table">
                <thead>
                  <tr
                    className="pp-user-row"
                    onClick={() => setSketchDesignExpanded((v) => !v)}
                  >
                    <th>
                      <span
                        className={`pp-expand ${sketchDesignExpanded ? "expanded" : ""}`}
                        aria-hidden
                      >
                        ›
                      </span>
                      Эскизное проектирование
                    </th>
                  </tr>
                </thead>
              </table>
            </div>
          )}

          {sketchDesignExpanded && !isGeneralDesignOnlyUser && (
          <>
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr>
                  <th>Исполнитель</th>
                  <th
                    className="pp-money pp-user-list-sum-col"
                    title="По всем проектам за месяц: сумма «к выплате» с учётом корректировки руководителя"
                  >
                    К выплате (итого)
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isUserExpanded = expandedUsers.has(user.id);
                  const projects = userProjects[user.id];
                  const loadingProjects = loadingProjectsByUser[user.id];
                  const userName = [user.surname, user.name].filter(Boolean).join(" ") || `Специалист ID ${user.id}`;
                  const approval = approvalByUserId[user.id] ?? null;
                  const isLockedByApproval = isUserOnApproval(user.id);
                  const approvalStatusText =
                    approvalByUserId[user.id] === undefined
                      ? "Проверка..."
                      : approval
                      ? getApprovalStatusText(approval)
                      : "Не отправлено";
                  const rowTot = userAllProjectsTotals[user.id];
                  const rowTotalLoading = !rowTot || rowTot.loading;
                  const rowTotalTitle =
                    rowTot &&
                    !rowTot.loading &&
                    !rowTot.error &&
                    rowTot.byEmployee != null &&
                    rowTot.byCorrection != null
                      ? `По данным сотрудника: ₸ ${formatMoney(rowTot.byEmployee)}\nС корректировкой руководителя: ₸ ${formatMoney(rowTot.byCorrection)}`
                      : undefined;
                  return (
                    <React.Fragment key={user.id}>
                      <tr
                        className="pp-user-row"
                        onClick={() => toggleUser(user.id)}
                      >
                        <td>
                          <span
                            className={`pp-expand ${isUserExpanded ? "expanded" : ""}`}
                            aria-hidden
                          >
                            ›
                          </span>
                          {userName}
                          {renderApprovalStatusBadge(approval, approvalStatusText)}
                          {isLockedByApproval && (
                            <span
                              className="pp-lock-hint"
                              data-tooltip="Редактирование заблокировано: сотрудник на согласовании"
                              aria-label="Редактирование заблокировано"
                            >
                              🔒
                            </span>
                          )}
                          {canDeleteApprovalForUser(user.id) && (
                            <button
                              type="button"
                              className="pp-row-delete-approval-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteApprovalForUser(user);
                              }}
                              disabled={deletingApproval}
                              title="Удалить согласование"
                            >
                              Удалить
                            </button>
                          )}
                        </td>
                        <td
                          className="pp-money pp-user-row-total"
                          title={rowTotalTitle}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {rowTotalLoading ? (
                            <span className="pp-row-total-muted">…</span>
                          ) : rowTot.error ? (
                            "–"
                          ) : (
                            <>₸ {formatMoney(rowTot.byCorrection)}</>
                          )}
                        </td>
                      </tr>
                      {isUserExpanded && (() => {
                        const projectList = Array.isArray(projects) ? projects : [];
                        const hasAnyProjectExpanded = projectList.some(
                          (p) => expandedProjects.has(`${user.id}-${p.id}`)
                        );
                        return (
                        <tr className="pp-detail-row">
                          <td colSpan={2} className="pp-detail-cell">
                            <table
                              className={`pp-detail-table ${!hasAnyProjectExpanded ? "pp-detail-table--projects-only" : ""}`}
                            >
                              <tbody>
                                {loadingProjects ? (
                                  <tr>
                                    <td colSpan={8} className="pp-loading-cell">
                                      <Preloader />
                                    </td>
                                  </tr>
                                ) : (
                                projectList.map((project) => {
                                  const key = `${user.id}-${project.id}`;
                                  const isProjectExpanded = expandedProjects.has(key);
                                  const statsRaw = (userProjectStats[user.id] || {})[project.id];
                                  const loadingStats = loadingStatsByKey[key];
                                  const stats = Array.isArray(statsRaw) ? statsRaw : [];
                                  return (
                                    <React.Fragment key={key}>
                                      <tr
                                        className="pp-project-row"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleProject(user.id, project.id);
                                        }}
                                      >
                                        {hasAnyProjectExpanded ? (
                                          <td colSpan={9}>
                                            <span
                                              className={`pp-expand ${isProjectExpanded ? "expanded" : ""}`}
                                              aria-hidden
                                            >
                                              ›
                                            </span>
                                            {project.name || `ID ${project.id}`}
                                          </td>
                                        ) : (
                                          <td>
                                            <span
                                              className={`pp-expand ${isProjectExpanded ? "expanded" : ""}`}
                                              aria-hidden
                                            >
                                              ›
                                            </span>
                                            {project.name || `ID ${project.id}`}
                                          </td>
                                        )}
                                      </tr>
                                      {isProjectExpanded && (
                                        <>
                                          {loadingStats ? (
                                            <tr>
                                              <td colSpan={9} className="pp-loading-cell">
                                                <Preloader />
                                              </td>
                                            </tr>
                                          ) : (
                                          <>
                                          <tr className="pp-detail-header-row">
                                            <th>Вид работ</th>
                                            <th className="pp-money">Смета по договору</th>
                                            <th className="pp-money">Выплачено</th>
                                            <th>% доля сотрудника</th>
                                            <th>% выполнения (по данным от сотрудника)</th>
                                            <th className="pp-money">К выплате (по данным от сотрудника)</th>
                                            <th>% выполнения (корректировка руководителя)</th>
                                            <th className="pp-money">К выплате (корректировка руководителя)</th>
                                            <th>Комментарий</th>
                                          </tr>
                                          {stats.map((stat) => {
                                            const corr = stat.correction ?? {};
                                            const pctEmployee = stat.completion_percentage_by_employee ?? 0;
                                            const pctCorrection = corr.completion_percentage_after_correction ?? pctEmployee;
                                            const sumEmployee = stat.to_be_payed_by_employee ?? 0;
                                            const sumCorrection = corr.completion_sum_after_correction ?? sumEmployee;
                                            const wtName = stat.work_type?.name ?? "–";
                                            const sharePercent = stat.work_type?.share_percent ?? 0;
                                            const estimateContract = stat.estimate_by_contract ?? 0;
                                            const alreadyPayed =
                                              stat.already_payed ?? stat.work_type?.already_payed ?? 0;
                                            const estimateContractNum = Number(estimateContract) || 0;
                                            const alreadyPayedNum = Number(alreadyPayed) || 0;
                                            const sumEmployeeNum = Number(sumEmployee);
                                            const sumCorrectionNum = Number(sumCorrection);
                                            const isEmployeePayoutOverBudget =
                                              estimateContractNum > 0 &&
                                              Number.isFinite(sumEmployeeNum) &&
                                              alreadyPayedNum + sumEmployeeNum > estimateContractNum;
                                            const isCorrectionPayoutOverBudget =
                                              estimateContractNum > 0 &&
                                              Number.isFinite(sumCorrectionNum) &&
                                              alreadyPayedNum + sumCorrectionNum > estimateContractNum;
                                            const maxAllowedPayout = Math.max(
                                              0,
                                              estimateContractNum - alreadyPayedNum,
                                            );
                                            const isSaving = savingId === corr.id;
                                            const isLockedByApproval = isUserOnApproval(user.id);
                                            return (
                                              <tr key={stat.work_type?.id ?? wtName} className="pp-work-row">
                                                <td>{wtName}</td>
                                                <td className="pp-money">₸ {formatMoney(estimateContract)}</td>
                                                <td className="pp-money">₸ {formatMoney(alreadyPayed)}</td>
                                                <td>{sharePercent}%</td>
                                                <td>
                                                  <div className="pp-progress-wrap">
                                                    <div className="pp-progress-bar">
                                                      <div
                                                        className="pp-progress-fill"
                                                        style={{ width: `${Math.min(100, pctEmployee)}%` }}
                                                      />
                                                    </div>
                                                    <span className="pp-progress-pct">{pctEmployee}%</span>
                                                  </div>
                                                </td>
                                                <td
                                                  className="pp-money"
                                                  style={
                                                    isEmployeePayoutOverBudget
                                                      ? { color: "#d32f2f", fontWeight: 600 }
                                                      : undefined
                                                  }
                                                  title={
                                                    isEmployeePayoutOverBudget
                                                      ? "Превышение: Выплачено + К выплате больше сметы по договору"
                                                      : undefined
                                                  }
                                                >
                                                  ₸ {formatMoney(sumEmployee)}
                                                  {isEmployeePayoutOverBudget && (
                                                    <div style={{ color: "#d32f2f", fontWeight: 600 }}>
                                                      Макс: ₸ {formatMoney(maxAllowedPayout)}
                                                    </div>
                                                  )}
                                                </td>
                                                <td>
                                                  <div className="pp-progress-wrap">
                                                    <div className="pp-progress-bar">
                                                      <div
                                                        className="pp-progress-fill"
                                                        style={{ width: `${Math.min(100, pctCorrection)}%` }}
                                                      />
                                                    </div>
                                                    <span className="pp-progress-pct">{pctCorrection}%</span>
                                                    {!isSketchReadOnly && (
                                                      <button
                                                        type="button"
                                                        className="pp-edit-btn"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          openConfirmPercent(stat, user);
                                                        }}
                                                        disabled={isSaving || isLockedByApproval}
                                                        title={
                                                          isLockedByApproval
                                                            ? "Редактирование недоступно: сотрудник на согласовании"
                                                            : "Изменить процент"
                                                        }
                                                      >
                                                        ✎
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                                <td
                                                  className="pp-money"
                                                  style={
                                                    isCorrectionPayoutOverBudget
                                                      ? { color: "#d32f2f", fontWeight: 600 }
                                                      : undefined
                                                  }
                                                  title={
                                                    isCorrectionPayoutOverBudget
                                                      ? "Превышение: Выплачено + К выплате больше сметы по договору"
                                                      : undefined
                                                  }
                                                >
                                                  ₸ {formatMoney(sumCorrection)}
                                                  {isCorrectionPayoutOverBudget && (
                                                    <div style={{ color: "#d32f2f", fontWeight: 600 }}>
                                                      Макс: ₸ {formatMoney(maxAllowedPayout)}
                                                    </div>
                                                  )}
                                                  {!isSketchReadOnly && (
                                                    <button
                                                      type="button"
                                                      className="pp-edit-btn"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        openConfirmSum(stat, user);
                                                      }}
                                                      disabled={isSaving || isLockedByApproval}
                                                      title={
                                                        isLockedByApproval
                                                          ? "Редактирование недоступно: сотрудник на согласовании"
                                                          : "Изменить сумму"
                                                      }
                                                    >
                                                      ✎
                                                    </button>
                                                  )}
                                                </td>
                                                <td>
                                                  {corr.comment ? (
                                                    <span title={corr.comment}>{corr.comment.slice(0, 30)}…</span>
                                                  ) : (
                                                    "–"
                                                  )}
                                                  {!isSketchReadOnly && (
                                                    <button
                                                      type="button"
                                                      className="pp-edit-btn"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        openCommentModal(stat);
                                                      }}
                                                      disabled={isSaving || isLockedByApproval}
                                                      title={
                                                        isLockedByApproval
                                                          ? "Редактирование недоступно: сотрудник на согласовании"
                                                          : "Комментарий"
                                                      }
                                                    >
                                                      ✎
                                                    </button>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                          </>
                                          )}
                                        </>
                                      )}
                                    </React.Fragment>
                                  );
                                })
                                )}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                        );
                      })()}
                    </React.Fragment>
                  );
                })}
                <tr className="pp-total-row">
                  <td className="pp-total-cell" colSpan={2}>
                    ИТОГО: по данным сотрудника ₸ {formatMoney(grandTotals.byEmployee)}, корректировка руководителя ₸ {formatMoney(grandTotals.byCorrection)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {!isSketchReadOnly && (
            <div className="pp-actions">
              <button
                type="button"
                className="pp-btn pp-btn-primary"
                onClick={openSendApprovalModal}
              >
                Отправить на согласование
              </button>
            </div>
          )}
          </>
          )}

          <GeneralDesignSection
            isAdms={canViewGeneralDesign}
            isReadOnly={isGeneralDesignReadOnly}
            month={month}
            year={year}
            savingId={savingId}
            setSavingId={setSavingId}
            setConfirmModal={setConfirmModal}
            setCommentModal={setCommentModal}
            formatMoney={formatMoney}
          />
        </>
      )}

      {!loadingError &&
        grouping === GROUP_BY_PROJECTS &&
        (projects.length > 0 || isGeneralDesignOnlyUser) && (
        <>
          {!isGeneralDesignOnlyUser && (
            <div className="pp-table-wrap">
              <table className="pp-table">
                <thead>
                  <tr
                    className="pp-user-row"
                    onClick={() => setSketchDesignExpanded((v) => !v)}
                  >
                    <th>
                      <span
                        className={`pp-expand ${sketchDesignExpanded ? "expanded" : ""}`}
                        aria-hidden
                      >
                        ›
                      </span>
                      Эскизное проектирование
                    </th>
                  </tr>
                </thead>
              </table>
            </div>
          )}

          {sketchDesignExpanded && !isGeneralDesignOnlyUser && (
          <>
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr>
                  <th>Проекты</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => {
                  const isTopProjectExpanded = expandedTopProjects.has(project.id);
                  const usersByProject = projectUsers[project.id];
                  const loadingUsers = loadingUsersByProject[project.id];
                  const projectName = project.name || `Проект ID ${project.id}`;
                  return (
                    <React.Fragment key={project.id}>
                      <tr
                        className="pp-user-row"
                        onClick={() => toggleTopProject(project.id)}
                      >
                        <td>
                          <span
                            className={`pp-expand ${isTopProjectExpanded ? "expanded" : ""}`}
                            aria-hidden
                          >
                            ›
                          </span>
                          {projectName}
                        </td>
                      </tr>
                      {isTopProjectExpanded && (() => {
                        const userList = Array.isArray(usersByProject) ? usersByProject : [];
                        const hasAnyUserExpanded = userList.some((u) =>
                          expandedProjects.has(`${u.id}-${project.id}`)
                        );
                        return (
                          <tr className="pp-detail-row">
                            <td colSpan={1} className="pp-detail-cell">
                              <table
                                className={`pp-detail-table ${!hasAnyUserExpanded ? "pp-detail-table--projects-only" : ""}`}
                              >
                                <tbody>
                                  {loadingUsers ? (
                                    <tr>
                                      <td colSpan={9} className="pp-loading-cell">
                                        <Preloader />
                                      </td>
                                    </tr>
                                  ) : (
                                    userList.map((user) => {
                                      const key = `${user.id}-${project.id}`;
                                      const isExpanded = expandedProjects.has(key);
                                      const statsRaw = (userProjectStats[user.id] || {})[project.id];
                                      const loadingStats = loadingStatsByKey[key];
                                      const stats = Array.isArray(statsRaw) ? statsRaw : [];
                                      const userName =
                                        [user.surname, user.name].filter(Boolean).join(" ") ||
                                        `Специалист ID ${user.id}`;
                                      const approval = approvalByUserId[user.id] ?? null;
                                      const isLockedByApproval = isUserOnApproval(user.id);
                                      const approvalStatusText =
                                        approvalByUserId[user.id] === undefined
                                          ? "Проверка..."
                                          : approval
                                          ? getApprovalStatusText(approval)
                                          : "Не отправлено";
                                      return (
                                        <React.Fragment key={key}>
                                          <tr
                                            className="pp-project-row"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleProject(user.id, project.id);
                                            }}
                                          >
                                            {hasAnyUserExpanded ? (
                                              <td colSpan={9}>
                                                <span
                                                  className={`pp-expand ${isExpanded ? "expanded" : ""}`}
                                                  aria-hidden
                                                >
                                                  ›
                                                </span>
                                                {userName}
                                                {renderApprovalStatusBadge(
                                                  approval,
                                                  approvalStatusText,
                                                )}
                                                {isLockedByApproval && (
                                                  <span
                                                    className="pp-lock-hint"
                                                    data-tooltip="Редактирование заблокировано: сотрудник на согласовании"
                                                    aria-label="Редактирование заблокировано"
                                                  >
                                                    🔒
                                                  </span>
                                                )}
                                                {canDeleteApprovalForUser(user.id) && (
                                                  <button
                                                    type="button"
                                                    className="pp-row-delete-approval-btn"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      deleteApprovalForUser(user);
                                                    }}
                                                    disabled={deletingApproval}
                                                    title="Удалить согласование"
                                                  >
                                                    Удалить
                                                  </button>
                                                )}
                                              </td>
                                            ) : (
                                              <td>
                                                <span
                                                  className={`pp-expand ${isExpanded ? "expanded" : ""}`}
                                                  aria-hidden
                                                >
                                                  ›
                                                </span>
                                                {userName}
                                                {renderApprovalStatusBadge(
                                                  approval,
                                                  approvalStatusText,
                                                )}
                                                {isLockedByApproval && (
                                                  <span
                                                    className="pp-lock-hint"
                                                    data-tooltip="Редактирование заблокировано: сотрудник на согласовании"
                                                    aria-label="Редактирование заблокировано"
                                                  >
                                                    🔒
                                                  </span>
                                                )}
                                                {canDeleteApprovalForUser(user.id) && (
                                                  <button
                                                    type="button"
                                                    className="pp-row-delete-approval-btn"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      deleteApprovalForUser(user);
                                                    }}
                                                    disabled={deletingApproval}
                                                    title="Удалить согласование"
                                                  >
                                                    Удалить
                                                  </button>
                                                )}
                                              </td>
                                            )}
                                          </tr>
                                          {isExpanded && (
                                            <>
                                              {loadingStats ? (
                                                <tr>
                                                  <td colSpan={9} className="pp-loading-cell">
                                                    <Preloader />
                                                  </td>
                                                </tr>
                                              ) : (
                                                <>
                                                  <tr className="pp-detail-header-row">
                                                    <th>Вид работ</th>
                                                    <th className="pp-money">Смета по договору</th>
                                                    <th className="pp-money">Выплачено</th>
                                                    <th>% доля сотрудника</th>
                                                    <th>% выполнения (по данным от сотрудника)</th>
                                                    <th className="pp-money">К выплате (по данным от сотрудника)</th>
                                                    <th>% выполнения (корректировка руководителя)</th>
                                                    <th className="pp-money">К выплате (корректировка руководителя)</th>
                                                    <th>Комментарий</th>
                                                  </tr>
                                                  {stats.map((stat) => {
                                                    const corr = stat.correction ?? {};
                                                    const pctEmployee = stat.completion_percentage_by_employee ?? 0;
                                                    const pctCorrection =
                                                      corr.completion_percentage_after_correction ?? pctEmployee;
                                                    const sumEmployee = stat.to_be_payed_by_employee ?? 0;
                                                    const sumCorrection =
                                                      corr.completion_sum_after_correction ?? sumEmployee;
                                                    const wtName = stat.work_type?.name ?? "–";
                                                    const sharePercent = stat.work_type?.share_percent ?? 0;
                                                    const estimateContract = stat.estimate_by_contract ?? 0;
                                                    const alreadyPayed =
                                                      stat.already_payed ?? stat.work_type?.already_payed ?? 0;
                                                    const estimateContractNum = Number(estimateContract) || 0;
                                                    const alreadyPayedNum = Number(alreadyPayed) || 0;
                                                    const sumEmployeeNum = Number(sumEmployee);
                                                    const sumCorrectionNum = Number(sumCorrection);
                                                    const isEmployeePayoutOverBudget =
                                                      estimateContractNum > 0 &&
                                                      Number.isFinite(sumEmployeeNum) &&
                                                      alreadyPayedNum + sumEmployeeNum > estimateContractNum;
                                                    const isCorrectionPayoutOverBudget =
                                                      estimateContractNum > 0 &&
                                                      Number.isFinite(sumCorrectionNum) &&
                                                      alreadyPayedNum + sumCorrectionNum > estimateContractNum;
                                                    const maxAllowedPayout = Math.max(
                                                      0,
                                                      estimateContractNum - alreadyPayedNum,
                                                    );
                                                    const isSaving = savingId === corr.id;
                                                    const isLockedByApproval = isUserOnApproval(user.id);
                                                    return (
                                                      <tr key={stat.work_type?.id ?? wtName} className="pp-work-row">
                                                        <td>{wtName}</td>
                                                        <td className="pp-money">₸ {formatMoney(estimateContract)}</td>
                                                        <td className="pp-money">₸ {formatMoney(alreadyPayed)}</td>
                                                        <td>{sharePercent}%</td>
                                                        <td>
                                                          <div className="pp-progress-wrap">
                                                            <div className="pp-progress-bar">
                                                              <div
                                                                className="pp-progress-fill"
                                                                style={{ width: `${Math.min(100, pctEmployee)}%` }}
                                                              />
                                                            </div>
                                                            <span className="pp-progress-pct">{pctEmployee}%</span>
                                                          </div>
                                                        </td>
                                                        <td
                                                          className="pp-money"
                                                          style={
                                                            isEmployeePayoutOverBudget
                                                              ? { color: "#d32f2f", fontWeight: 600 }
                                                              : undefined
                                                          }
                                                          title={
                                                            isEmployeePayoutOverBudget
                                                              ? "Превышение: Выплачено + К выплате больше сметы по договору"
                                                              : undefined
                                                          }
                                                        >
                                                          ₸ {formatMoney(sumEmployee)}
                                                          {isEmployeePayoutOverBudget && (
                                                            <div style={{ color: "#d32f2f", fontWeight: 600 }}>
                                                              Макс: ₸ {formatMoney(maxAllowedPayout)}
                                                            </div>
                                                          )}
                                                        </td>
                                                        <td>
                                                          <div className="pp-progress-wrap">
                                                            <div className="pp-progress-bar">
                                                              <div
                                                                className="pp-progress-fill"
                                                                style={{ width: `${Math.min(100, pctCorrection)}%` }}
                                                              />
                                                            </div>
                                                            <span className="pp-progress-pct">{pctCorrection}%</span>
                                                            {!isSketchReadOnly && (
                                                              <button
                                                                type="button"
                                                                className="pp-edit-btn"
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  openConfirmPercent(stat, user);
                                                                }}
                                                                disabled={isSaving || isLockedByApproval}
                                                                title={
                                                                  isLockedByApproval
                                                                    ? "Редактирование недоступно: сотрудник на согласовании"
                                                                    : "Изменить процент"
                                                                }
                                                              >
                                                                ✎
                                                              </button>
                                                            )}
                                                          </div>
                                                        </td>
                                                        <td
                                                          className="pp-money"
                                                          style={
                                                            isCorrectionPayoutOverBudget
                                                              ? { color: "#d32f2f", fontWeight: 600 }
                                                              : undefined
                                                          }
                                                          title={
                                                            isCorrectionPayoutOverBudget
                                                              ? "Превышение: Выплачено + К выплате больше сметы по договору"
                                                              : undefined
                                                          }
                                                        >
                                                          ₸ {formatMoney(sumCorrection)}
                                                          {isCorrectionPayoutOverBudget && (
                                                            <div style={{ color: "#d32f2f", fontWeight: 600 }}>
                                                              Макс: ₸ {formatMoney(maxAllowedPayout)}
                                                            </div>
                                                          )}
                                                          {!isSketchReadOnly && (
                                                            <button
                                                              type="button"
                                                              className="pp-edit-btn"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                openConfirmSum(stat, user);
                                                              }}
                                                              disabled={isSaving || isLockedByApproval}
                                                              title={
                                                                isLockedByApproval
                                                                  ? "Редактирование недоступно: сотрудник на согласовании"
                                                                  : "Изменить сумму"
                                                              }
                                                            >
                                                              ✎
                                                            </button>
                                                          )}
                                                        </td>
                                                        <td>
                                                          {corr.comment ? (
                                                            <span title={corr.comment}>{corr.comment.slice(0, 30)}…</span>
                                                          ) : (
                                                            "–"
                                                          )}
                                                          {!isSketchReadOnly && (
                                                            <button
                                                              type="button"
                                                              className="pp-edit-btn"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                openCommentModal(stat);
                                                              }}
                                                              disabled={isSaving || isLockedByApproval}
                                                              title={
                                                                isLockedByApproval
                                                                  ? "Редактирование недоступно: сотрудник на согласовании"
                                                                  : "Комментарий"
                                                              }
                                                            >
                                                              ✎
                                                            </button>
                                                          )}
                                                        </td>
                                                      </tr>
                                                    );
                                                  })}
                                                </>
                                              )}
                                            </>
                                          )}
                                        </React.Fragment>
                                      );
                                    })
                                  )}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        );
                      })()}
                    </React.Fragment>
                  );
                })}
                <tr className="pp-total-row">
                  <td className="pp-total-cell">
                    ИТОГО: по данным сотрудника ₸ {formatMoney(grandTotals.byEmployee)}, корректировка руководителя ₸ {formatMoney(grandTotals.byCorrection)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {!isSketchReadOnly && (
            <div className="pp-actions">
              <button
                type="button"
                className="pp-btn pp-btn-primary"
                onClick={openSendApprovalModal}
              >
                Отправить на согласование
              </button>
            </div>
          )}
          </>
          )}

          <GeneralDesignSection
            isAdms={canViewGeneralDesign}
            isReadOnly={isGeneralDesignReadOnly}
            month={month}
            year={year}
            savingId={savingId}
            setSavingId={setSavingId}
            setConfirmModal={setConfirmModal}
            setCommentModal={setCommentModal}
            formatMoney={formatMoney}
          />
        </>
      )}

      {/* Модалка подтверждения изменения % или суммы */}
      {sendApprovalModalOpen && (
        <div
          className="pp-modal-overlay"
          onClick={() => !sendingApproval && setSendApprovalModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pp-send-approval-title"
        >
          <div className="pp-modal pp-send-approval-modal" onClick={(e) => e.stopPropagation()}>
            <div id="pp-send-approval-title" className="pp-modal-title">
              Отправка прогресса выплат на согласование
            </div>
            <div className="pp-modal-body">
              <label className="pp-modal-label">Сотрудник (фигурант):</label>
              <Select
                styles={{
                  ...selectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                  menu: (base) => ({ ...base, zIndex: 2000 }),
                }}
                options={approvalUserOptions}
                value={selectedApprovalUser}
                onChange={setSelectedApprovalUser}
                placeholder="Выберите сотрудника"
                isClearable
                className="pp-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {hasExistingApproval && (
                <div className="pp-existing-approval-note">
                  Для этого сотрудника уже создано согласование за {MONTH_NAMES[month - 1]} {year}
                  {" "}(статус: {getApprovalStatusText(selectedUserApproval)}).
                  {canDeleteExistingApproval && (
                    <button
                      type="button"
                      className="pp-existing-approval-delete-btn"
                      onClick={deleteExistingApproval}
                      disabled={deletingApproval}
                    >
                      {deletingApproval ? "Удаление..." : "Удалить согласование"}
                    </button>
                  )}
                </div>
              )}

              <label className="pp-modal-label pp-modal-label--mt">
                Кому отправить на согласование:
              </label>
              <Select
                styles={{
                  ...selectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                  menu: (base) => ({ ...base, zIndex: 2000 }),
                }}
                options={approverOptions}
                value={selectedApprovers}
                onChange={(opts) => setSelectedApprovers(opts || [])}
                placeholder="Выберите согласующих"
                isMulti
                className="pp-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            <div className="pp-modal-actions">
              <button
                type="button"
                className="pp-btn pp-btn-secondary"
                onClick={() => setSendApprovalModalOpen(false)}
                disabled={sendingApproval}
              >
                Отмена
              </button>
              <button
                type="button"
                className="pp-btn pp-btn-primary"
                onClick={submitSendApproval}
                disabled={sendingApproval || hasExistingApproval}
              >
                {sendingApproval ? "Отправка..." : "Отправить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка подтверждения изменения % или суммы */}
      {confirmModal.open && (
        <div
          className="pp-modal-overlay"
          onClick={handleConfirmOverlayClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pp-confirm-title"
        >
          <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
            <div id="pp-confirm-title" className="pp-modal-title">
              {confirmModal.title}
            </div>
            <div className="pp-modal-subtitle">
              Вы уверены, что хотите изменить{" "}
              {confirmModal.type === "percent" ? "процент выполнения" : "сумму к выплате"} по виду
              работ «{confirmModal.workTypeName}» для исполнителя «{confirmModal.userName}»?
            </div>
            <div className="pp-modal-body">
              <label className="pp-modal-label">
                {confirmModal.type === "percent" ? "Новый процент (0–100):" : "Новая сумма:"}
              </label>
              {confirmModal.type === "percent" ? (
                <div className="pp-field-with-slider">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={0.5}
                    onPointerDown={lockConfirmOverlayCloseDuringSliderDrag}
                    value={
                      (() => {
                        const n = Number(confirmModal.newValue);
                        return Number.isNaN(n) ? 0 : Math.min(100, Math.max(0, n));
                      })()
                    }
                    onChange={(e) =>
                      setConfirmModal((m) => ({ ...m, newValue: e.target.value }))
                    }
                    className="pp-range"
                  />
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    onPointerDown={lockConfirmOverlayCloseDuringSliderDrag}
                    onFocus={selectConfirmInputValueOnFocus}
                    value={confirmModal.newValue}
                    onChange={(e) =>
                      setConfirmModal((m) => ({ ...m, newValue: e.target.value }))
                    }
                    className="pp-input pp-input-number"
                  />
                </div>
              ) : (
                <div className="pp-field-with-slider">
                  <input
                    type="range"
                    min={0}
                    max={Math.max(Number(confirmModal.estimateContract) || 0, 1)}
                    step={1000}
                    onPointerDown={lockConfirmOverlayCloseDuringSliderDrag}
                    value={
                      (() => {
                        const n = Number(String(confirmModal.newValue).replace(/\s/g, ""));
                        const max = Math.max(Number(confirmModal.estimateContract) || 0, 1);
                        return Number.isNaN(n) ? 0 : Math.min(max, Math.max(0, n));
                      })()
                    }
                    onChange={(e) =>
                      setConfirmModal((m) => ({ ...m, newValue: e.target.value }))
                    }
                    className="pp-range"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    onPointerDown={lockConfirmOverlayCloseDuringSliderDrag}
                    onFocus={selectConfirmInputValueOnFocus}
                    value={confirmModal.newValue}
                    onChange={(e) =>
                      setConfirmModal((m) => ({ ...m, newValue: e.target.value }))
                    }
                    className="pp-input pp-input-sum"
                    placeholder={`Макс. ₸ ${formatMoney(confirmModal.estimateContract || 0)}`}
                  />
                </div>
              )}
            </div>
            <div className="pp-modal-actions">
              <button
                type="button"
                className="pp-btn pp-btn-secondary"
                onClick={() => setConfirmModal((m) => ({ ...m, open: false }))}
              >
                Отмена
              </button>
              <button
                type="button"
                className="pp-btn pp-btn-primary"
                onClick={confirmModalConfirm}
              >
                Изменить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка комментария */}
      {commentModal.open && (
        <div
          className="pp-modal-overlay"
          onClick={() => setCommentModal((m) => ({ ...m, open: false }))}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pp-comment-title"
        >
          <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
            <div id="pp-comment-title" className="pp-modal-title">
              Комментарии по работе
            </div>
            <div className="pp-modal-subtitle">{commentModal.workTypeName}</div>
            <div className="pp-modal-body">
              <textarea
                className="pp-comment-input"
                placeholder="Введите комментарий..."
                value={commentInput}
                onChange={(e) => setCommentInput(e.target.value)}
                aria-label="Комментарий"
                rows={4}
              />
            </div>
            <div className="pp-modal-actions">
              <button
                type="button"
                className="pp-btn pp-btn-secondary"
                onClick={() => setCommentModal((m) => ({ ...m, open: false }))}
              >
                Отмена
              </button>
              <button
                type="button"
                className="pp-btn pp-btn-primary"
                onClick={commentModalSave}
              >
                + Добавить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsProgress;
